import React, { useState } from "react";
import { theme } from "antd";
import { space } from "../theme/space";

/**
 * Page title + supporting line + actions, on one baseline.
 *
 * One h-level per page. The meta line carries what the title would otherwise
 * try to say, keeping the title short enough to read as a name.
 *
 * **Why this is a component and not a `<h2>`.** It used to be. Five list pages
 * rendered a bare `<h2>`, which — against an unstyled 16px body — the browser
 * drew at 21px/700 with 17.43px margins. Nobody chose any of those numbers,
 * they were `1.5em` and `0.83em`, they were heavier than every other heading in
 * the app, and they sat off the 4px grid the rest of the page uses. Meanwhile
 * six other pages used `Typography.Title level={4}` and Reports used this. Four
 * treatments for one role.
 *
 * The title is deliberately **not** a `Typography.Title`: antd's h-tags carry
 * their own margins and a fixed weight per level, and every existing call site
 * had already overridden them with `style={{ margin: 0 }}`.
 */
export const PageHeader: React.FC<{
  title: React.ReactNode;
  meta?: React.ReactNode;
  actions?: React.ReactNode;
}> = ({ title, meta, actions }) => {
  const { token } = theme.useToken();
  return (
    <div
      style={{
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "space-between",
        gap: space.base,
        flexWrap: "wrap",
        marginBottom: space.base,
      }}
    >
      <div style={{ minWidth: 0 }}>
        <div
          style={{
            fontSize: 20,
            fontWeight: 600,
            lineHeight: 1.3,
            color: token.colorText,
          }}
        >
          {title}
        </div>
        {meta && (
          <div
            style={{
              marginTop: space.hair,
              fontSize: 13,
              color: token.colorTextSecondary,
              display: "flex",
              alignItems: "center",
              gap: space.tight,
              flexWrap: "wrap",
            }}
          >
            {meta}
          </div>
        )}
      </div>
      {actions && (
        <div style={{ display: "flex", gap: space.tight, flexShrink: 0 }}>
          {actions}
        </div>
      )}
    </div>
  );
};

/**
 * A slot in the header's actions area for a control that lives somewhere else.
 *
 * `ManageableTable` owns its Columns button — column visibility, widths and
 * their stored prefs are all its state — but the button belongs beside the page
 * title, not floating above the table. The hook hands back the element to
 * render in `actions` and the DOM node to portal into; a callback ref rather
 * than `useRef`, because the target has to arrive as a *state* change or the
 * table renders once against `null` and never learns the node exists.
 */
export function useHeaderSlot() {
  const [target, setTarget] = useState<HTMLDivElement | null>(null);
  const slot = (
    <div
      ref={setTarget}
      style={{ display: "flex", alignItems: "center", gap: space.tight }}
    />
  );
  return { slot, target };
}

/** A `·` between meta items — cheaper than a border and reads as one line. */
export const MetaDot = () => {
  const { token } = theme.useToken();
  return <span style={{ color: token.colorTextQuaternary }}>·</span>;
};

export default PageHeader;
